import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Plot, Point, Line, Text, Theme } from 'mafs';
import { Slider } from 'antd';

const formatNum = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '0';
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
};

export const QuadraticExplorer = ({ config }) => {
    const [a, setA] = useState(config.a || 1);
    const [b, setB] = useState(config.b || -2);
    const [c, setC] = useState(config.c || -3);
    const [form, setForm] = useState(config.form || 'standard');

    const vertex = useMemo(() => {
        const h = -b / (2 * a);
        const k = a * h * h + b * h + c;
        return { h, k };
    }, [a, b, c]);

    const discriminant = useMemo(() => b * b - 4 * a * c, [a, b, c]);

    const roots = useMemo(() => {
        if (discriminant < 0) return null;
        const sqrtD = Math.sqrt(discriminant);
        return { x1: (-b + sqrtD) / (2 * a), x2: (-b - sqrtD) / (2 * a) };
    }, [a, b, discriminant]);

    const yIntercept = c;

    const renderGraph = () => {
        const viewBoxYMin = Math.min(vertex.k - 5, yIntercept - 2, 0);
        const viewBoxYMax = Math.max(vertex.k + 10, yIntercept + 2, 10);

        return (
            <div style={{ marginTop: '20px' }}>
                <Mafs viewBox={{ x: [-10, 10], y: [viewBoxYMin, viewBoxYMax] }} height={400} pan={false}>
                    <Coordinates.Cartesian />

                    <Plot.OfX y={(x) => a * x * x + b * x + c} color={Theme.blue} weight={3} />

                    {config.showVertex !== false && (
                        <>
                            <Point x={vertex.h} y={vertex.k} color={Theme.red} />
                            <Text x={vertex.h} y={vertex.k - 0.8}>V({formatNum(vertex.h)}, {formatNum(vertex.k)})</Text>
                        </>
                    )}

                    {config.showAxisOfSymmetry !== false && (
                        <Line.Segment point1={[vertex.h, viewBoxYMin]} point2={[vertex.h, viewBoxYMax]} color={Theme.green} style="dashed" />
                    )}

                    {config.showRoots !== false && roots && (
                        <>
                            <Point x={roots.x1} y={0} color={Theme.orange} />
                            <Point x={roots.x2} y={0} color={Theme.orange} />
                            <Text x={roots.x1} y={-1}>x₁={formatNum(roots.x1)}</Text>
                            {Math.abs(roots.x1 - roots.x2) > 1 && <Text x={roots.x2} y={-1}>x₂={formatNum(roots.x2)}</Text>}
                        </>
                    )}

                    {config.showYIntercept !== false && (
                        <>
                            <Point x={0} y={yIntercept} color={Theme.indigo} />
                            <Text x={0.5} y={yIntercept}>y={formatNum(yIntercept)}</Text>
                        </>
                    )}
                </Mafs>
            </div>
        );
    };

    return (
        <div className="ch-quadratic-explorer">
            <div style={{ padding: '15px', background: '#f0f5ff', borderRadius: '8px', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>Quadratic Function</h3>
                <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#1890ff' }}>
                    {form === 'standard' && <>y = ax² + bx + c</>}
                    {form === 'vertex' && <>y = a(x - h)² + k</>}
                    {form === 'factored' && <>y = a(x - x₁)(x - x₂)</>}
                </div>
            </div>

            <div className="ch-controls" style={{ padding: '15px', background: '#fafafa', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        Coefficient a: {formatNum(a)}
                    </label>
                    <Slider
                        min={-3}
                        max={3}
                        step={0.25}
                        value={a}
                        onChange={setA}
                        marks={{ '-3': '-3', '0': '0', '3': '3' }}
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        Coefficient b: {formatNum(b)}
                    </label>
                    <Slider
                        min={-10}
                        max={10}
                        step={1}
                        value={b}
                        onChange={setB}
                        marks={{ '-10': '-10', '0': '0', '10': '10' }}
                    />
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                        Coefficient c: {formatNum(c)}
                    </label>
                    <Slider
                        min={-10}
                        max={10}
                        step={1}
                        value={c}
                        onChange={setC}
                        marks={{ '-10': '-10', '0': '0', '10': '10' }}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                {['standard', 'vertex', 'factored'].map(f => (
                    <button key={f} onClick={() => setForm(f)} style={{ padding: '8px 16px', background: form === f ? '#1890ff' : '#fff', color: form === f ? 'white' : '#000', border: '1px solid #91d5ff', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                        {f.charAt(0).toUpperCase() + f.slice(1)} Form
                    </button>
                ))}
            </div>

            <div style={{ padding: '15px', background: '#f9f9f9', borderRadius: '8px', fontFamily: 'monospace', fontSize: '16px', marginBottom: '20px' }}>
                {form === 'standard' && <div>y = {formatNum(a)}x² {b >= 0 ? '+' : ''} {formatNum(b)}x {c >= 0 ? '+' : ''} {formatNum(c)}</div>}
                {form === 'vertex' && <div>y = {formatNum(a)}(x {vertex.h >= 0 ? '-' : '+'} {formatNum(Math.abs(vertex.h))})² {vertex.k >= 0 ? '+' : ''} {formatNum(vertex.k)}</div>}
                {form === 'factored' && (
                    roots ? <div>y = {formatNum(a)}(x {roots.x1 >= 0 ? '-' : '+'} {formatNum(Math.abs(roots.x1))})(x {roots.x2 >= 0 ? '-' : '+'} {formatNum(Math.abs(roots.x2))})</div> : <div>No real roots (cannot factor over ℝ)</div>
                )}
            </div>

            {renderGraph()}

            <div style={{ marginTop: '20px', padding: '20px', background: '#fff7e6', border: '2px solid #ffc53d', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, color: '#d48806' }}>📊 Properties</h4>
                <div style={{ lineHeight: '1.8' }}>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        <strong>Vertex:</strong> ({formatNum(vertex.h)}, {formatNum(vertex.k)})
                    </p>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        <strong>Axis of Symmetry:</strong> x = {formatNum(vertex.h)}
                    </p>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        <strong>Y-Intercept:</strong> ({0}, {formatNum(yIntercept)})
                    </p>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        <strong>Discriminant (Δ):</strong> {formatNum(discriminant)}
                    </p>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        <strong>Roots:</strong> {discriminant > 0 ? `x₁ = ${formatNum(roots.x1)}, x₂ = ${formatNum(roots.x2)}` : discriminant === 0 ? `x = ${formatNum(-b / (2 * a))} (repeated)` : 'No real roots'}
                    </p>
                    <p style={{ margin: '5px 0', fontFamily: 'monospace', fontSize: '14px' }}>
                        <strong>Concavity:</strong> {a > 0 ? 'Opens upward (∪)' : 'Opens downward (∩)'}
                    </p>
                </div>
            </div>
        </div>
    );
};